import { api, saveBlob } from './client';
import { endpoints } from './endpoints';

// -- overview ----------------------------------------------------------------
export function getOverview() {
  return api.get(endpoints.admin.overview);
}

// -- company verification ----------------------------------------------------
export function listVerifications(params) {
  return api.getPaginated(endpoints.admin.verifications, { params });
}

export function approveCompany(id) {
  return api.post(endpoints.admin.approveCompany(id));
}

export function rejectCompany(id, reason) {
  return api.post(endpoints.admin.rejectCompany(id), { reason });
}

export function setCompanyFeatured(id, isFeatured) {
  return api.post(endpoints.admin.featureCompany(id), { is_featured: isFeatured });
}

// -- student identity verification -------------------------------------------
// The company side above is a queue of submissions. A student submits nothing,
// so this is the student directory narrowed to the decision an admin has or
// has not made -- see docs/api.md.
export function listStudentVerifications(params) {
  return api.getPaginated(endpoints.admin.studentVerifications, { params });
}

export function approveStudent(id) {
  return api.post(endpoints.admin.approveStudent(id));
}

export function rejectStudent(id, reason) {
  return api.post(endpoints.admin.rejectStudent(id), { reason });
}

// -- document verification ---------------------------------------------------
// Diplomas, certificates and CVs. `kind` is 'diploma' (the id is the
// education entry), 'certification' (the id is the certification) or 'resume'
// (the id is the CV). Opening a document is audited server-side.
export function listDocumentVerifications(params) {
  return api.getPaginated(endpoints.admin.documentVerifications, { params });
}

export function getDocumentVerificationCounts() {
  return api.get(endpoints.admin.documentVerificationCounts);
}

/** `inline: true` returns a link the browser displays instead of saving. */
export function getDocumentDownloadUrl(kind, id, { inline = false } = {}) {
  return api.get(endpoints.admin.documentDownload(kind, id), {
    params: inline ? { inline: true } : undefined,
  });
}

export function approveDocument(kind, id) {
  return api.post(endpoints.admin.approveDocument(kind, id));
}

export function rejectDocument(kind, id, reason) {
  return api.post(endpoints.admin.rejectDocument(kind, id), { reason });
}

// -- company directory -------------------------------------------------------
// Separate from the verification queue above: that one only ever holds what is
// waiting for a decision, this one holds every company on the platform.
export function listCompanies(params) {
  return api.getPaginated(endpoints.admin.companies, { params });
}

export function getCompany(id) {
  return api.get(endpoints.admin.companyById(id));
}

/** Reopen a company its owner closed, and the team logins the closure switched off. */
export function restoreCompany(id) {
  return api.post(endpoints.admin.companyRestore(id));
}

// -- student directory -------------------------------------------------------
export function listStudents(params) {
  return api.getPaginated(endpoints.admin.students, { params });
}

export function getStudent(id) {
  return api.get(endpoints.admin.studentById(id));
}

// -- recruiter directory -----------------------------------------------------
// A recruiter is a workspace seat, so the id here is the membership and the
// account behind it is `user_id` -- which is what setUserStatus below acts on.
export function listRecruiters(params) {
  return api.getPaginated(endpoints.admin.recruiters, { params });
}

export function getRecruiter(memberId) {
  return api.get(endpoints.admin.recruiterById(memberId));
}

// -- internship moderation ---------------------------------------------------
export function listInternshipQueue(params) {
  return api.getPaginated(endpoints.admin.internships, { params });
}

export function getInternship(id) {
  return api.get(endpoints.admin.internshipById(id));
}

export function approveInternship(id) {
  return api.post(endpoints.admin.approveInternship(id));
}

export function rejectInternship(id, reason) {
  return api.post(endpoints.admin.rejectInternship(id), { reason });
}

// -- recruitment activity ----------------------------------------------------
// Read-only: what companies and students are doing, across every tenant.
export function listApplications(params) {
  return api.getPaginated(endpoints.admin.recruitmentApplications, { params });
}

/** One application with its history, CV reference, company and listing. */
export function getApplication(id) {
  return api.get(endpoints.admin.recruitmentApplicationById(id));
}

export function listInterviews(params) {
  return api.getPaginated(endpoints.admin.recruitmentInterviews, { params });
}

/**
 * The letter a candidate received, as `{ blob, filename }`. Sent offers only;
 * each fetch is audited server-side.
 */
export function getOfferLetter(id) {
  return api.download(endpoints.admin.recruitmentOfferLetter(id));
}

export function listOffers(params) {
  return api.getPaginated(endpoints.admin.recruitmentOffers, { params });
}
// Rejection only ever worked on a listing waiting to go up. This is the other
// decision -- one that is already live and should not be. It moves the listing
// to CLOSED; there is no admin delete, deliberately (see docs/api.md).
export function closeInternship(id, reason) {
  return api.post(endpoints.admin.closeInternship(id), { reason });
}

// -- recruitment oversight ---------------------------------------------------
// Read-only on purpose: an admin watches the pipeline and never moves a
// candidate through it. Whose hiring decision it is has not changed.
// export function listApplications(params) {
//   return api.getPaginated(endpoints.admin.applications, { params });
// }

// -- users -------------------------------------------------------------------
export function listUsers(params) {
  return api.getPaginated(endpoints.admin.users, { params });
}

export function setUserStatus(id, status, reason) {
  return api.patch(endpoints.admin.userStatus(id), { status, reason });
}

// Does what entering the emailed code does, for someone who never got it.
export function verifyUserEmail(id) {
  return api.post(endpoints.admin.verifyEmail(id));
}

// Returns an access token for `id` and nothing else -- no refresh token, so the
// session expires on its own and cannot be extended. The admin's own tokens are
// untouched, which is why stopping is purely a client-side matter.
//
// `reason` is optional and omitted rather than sent empty: the audit row should
// carry a real explanation or none, not a blank string pretending to be one.
export function impersonate(id, reason) {
  return api.post(endpoints.admin.impersonate(id), reason ? { reason } : {});
}

// -- audit -------------------------------------------------------------------
export function listAuditLog(params) {
  return api.getPaginated(endpoints.admin.audit, { params });
}

// -- billing -----------------------------------------------------------------
export function listBillingPlans() {
  return api.get(endpoints.admin.billingPlans);
}

export function getBillingPlan(code) {
  return api.get(endpoints.admin.billingPlanByCode(code));
}

export function createBillingPlan(input) {
  return api.post(endpoints.admin.billingPlans, input);
}

export function updateBillingPlan(code, input) {
  return api.patch(endpoints.admin.billingPlanByCode(code), input);
}

export function listSubscriptions(params) {
  return api.getPaginated(endpoints.admin.billingSubscriptions, { params });
}

export function getSubscription(id) {
  return api.get(endpoints.admin.billingSubscriptionById(id));
}

// Support actions on a company's behalf. They go through the same service the
// company's own screen calls, so the billing rules are identical either way.
export function changeSubscriptionPlan(id, planCode, couponCode) {
  return api.post(endpoints.admin.billingSubscriptionPlan(id), {
    plan_code: planCode,
    ...(couponCode ? { coupon_code: couponCode } : {}),
  });
}

// Keyed on the company, not a subscription: a company on the free plan has no
// subscription row at all, and that is exactly who gets sold a plan.
export function assignSubscription({ companyId, planCode, couponCode }) {
  return api.post(endpoints.admin.billingSubscriptions, {
    company_id: companyId,
    plan_code: planCode,
    ...(couponCode ? { coupon_code: couponCode } : {}),
  });
}

export function cancelSubscription(id) {
  return api.post(endpoints.admin.billingSubscriptionCancel(id));
}

export function resumeSubscription(id) {
  return api.post(endpoints.admin.billingSubscriptionResume(id));
}

export function getBillingOverview() {
  return api.get(endpoints.admin.billingOverview);
}

export function listAllInvoices(params) {
  return api.getPaginated(endpoints.admin.billingInvoices, { params });
}

export function markInvoicePaid(id, reference) {
  return api.post(endpoints.admin.billingInvoiceMarkPaid(id), { reference });
}

// The one invoice on this platform a person creates. Everything else is issued
// by the renewal run, on a schedule nobody triggers. It bills exactly one
// customer: a company or a student.
export function issueInvoice({ companyId, studentId, description, amountCents, dueInDays }) {
  return api.post(endpoints.admin.billingInvoices, {
    ...(studentId ? { student_id: studentId } : { company_id: companyId }),
    description,
    amount_cents: amountCents,
    ...(dueInDays ? { due_in_days: dueInDays } : {}),
  });
}

export function voidInvoice(id, reason) {
  return api.post(endpoints.admin.billingInvoiceVoid(id), { reason });
}

/** Saves any invoice — a company's or a student's — as a PDF. */
export async function downloadInvoicePdf(invoice) {
  const { blob, filename } = await api.download(
    `${endpoints.admin.billingInvoices}/${invoice.id}/pdf`,
  );
  saveBlob(blob, filename.endsWith('.pdf') ? filename : `${invoice.number}.pdf`);
}

export function runRenewals() {
  return api.post(endpoints.admin.billingRunRenewals);
}

// -- student plans -----------------------------------------------------------
// A student plan starts when it is paid, so assigning one issues the plan's
// invoice; `markPaid` settles it at once ("Assigned by admin"). Paths are
// inlined here until they are added to endpoints.js.
const STUDENT_SUBSCRIPTIONS = '/admin/billing/student-subscriptions';

/** Keyed on the student: one on Free has no subscription row to open. */
export function getStudentBilling(studentId) {
  return api.get(`/admin/billing/students/${studentId}`);
}

export function listStudentSubscriptions(params) {
  return api.getPaginated(STUDENT_SUBSCRIPTIONS, { params });
}

export function getStudentSubscription(id) {
  return api.get(`${STUDENT_SUBSCRIPTIONS}/${id}`);
}

export function assignStudentPlan({ studentId, planCode, markPaid = false }) {
  return api.post(STUDENT_SUBSCRIPTIONS, {
    student_id: studentId,
    plan_code: planCode,
    mark_paid: markPaid,
  });
}

/** Takes effect now; the paid period keeps its end date. */
export function changeStudentPlan(id, planCode) {
  return api.patch(`${STUDENT_SUBSCRIPTIONS}/${id}/plan`, { plan_code: planCode });
}

export function cancelStudentSubscription(id) {
  return api.post(`${STUDENT_SUBSCRIPTIONS}/${id}/cancel`);
}

export function resumeStudentSubscription(id) {
  return api.post(`${STUDENT_SUBSCRIPTIONS}/${id}/resume`);
}

// -- ATS integrations --------------------------------------------------------
export function listAtsProviders() {
  return api.get(endpoints.admin.atsProviders);
}

export function listAtsConnections(params) {
  return api.getPaginated(endpoints.admin.atsConnections, { params });
}

export function runAtsHandovers() {
  return api.post(endpoints.admin.atsRun);
}

// -- messaging ---------------------------------------------------------------
// Metadata only. A conversation opens to moderation when a participant reports
// it, and reading one is audited.
export function listConversations(params) {
  return api.getPaginated(endpoints.admin.messages, { params });
}

export function getReportedConversation(id) {
  return api.get(endpoints.admin.messageById(id));
}

// -- reported content --------------------------------------------------------
// Two ways to close a report because they are not the same answer: resolved
// means the content was acted on, dismissed means it was looked at and is fine.
export function listReports(params) {
  return api.getPaginated(endpoints.admin.reports, { params });
}

export function resolveReport(id, note) {
  return api.post(endpoints.admin.resolveReport(id), { note });
}

export function dismissReport(id, note) {
  return api.post(endpoints.admin.dismissReport(id), { note });
}

// -- coupons -----------------------------------------------------------------
// No delete, deliberately: a redeemed coupon is part of why a company was
// charged what it was. Retiring it (`is_active: false`) stops it being
// accepted and leaves the record -- exactly as with a plan.
export function listCoupons(params) {
  return api.getPaginated(endpoints.admin.coupons, { params });
}

export function getCoupon(code) {
  return api.get(endpoints.admin.couponByCode(code));
}

export function createCoupon(input) {
  return api.post(endpoints.admin.coupons, input);
}

export function updateCoupon(code, input) {
  return api.patch(endpoints.admin.couponByCode(code), input);
}

export function listCouponRedemptions(code, params) {
  return api.getPaginated(endpoints.admin.couponRedemptions(code), { params });
}

// -- reference data ----------------------------------------------------------
// One set of functions for all four lists: `kind` is the path segment, and the
// only thing that differs between a university and a skill at this layer is the
// shape of the body the caller passes.
//
// `include_inactive` defaults to true here as it does on the server. This is
// the one screen where a retired entry is the thing somebody came to find.
export function listReference(kind, params) {
  return api.getPaginated(endpoints.admin.masterData(kind), { params });
}

export function createReference(kind, input) {
  return api.post(endpoints.admin.masterData(kind), input);
}

export function updateReference(kind, id, input) {
  return api.patch(endpoints.admin.masterDataItem(kind, id), input);
}

/** Retire or restore. Never a delete -- every row here is referenced. */
export function setReferenceActive(kind, id, isActive) {
  return api.post(endpoints.admin.masterDataState(kind, id, isActive ? 'restore' : 'retire'));
}

export function addSkillAlias(id, alias) {
  return api.post(endpoints.admin.skillAliases(id), { alias });
}

export function removeSkillAlias(id, aliasId) {
  return api.delete(endpoints.admin.skillAlias(id, aliasId));
}

/**
 * Fold a duplicate into a survivor.
 *
 * Not symmetric and not undoable: the skill in the path survives, everything
 * tagged with the other is re-tagged onto it. The caller is expected to
 * confirm first.
 */
export function mergeSkills(survivorId, duplicateId) {
  return api.post(endpoints.admin.skillMerge(survivorId), { duplicate_id: duplicateId });
}

/**
 * What every third-party seam is configured with.
 *
 * Configuration, not liveness: whether a vendor is answering right now is on
 * the health check, and a settings screen that probed to render itself would
 * hang exactly when somebody was looking at it because a vendor was down.
 */
export function getIntegrationSettings() {
  return api.get(endpoints.admin.integrationSettings);
}

/**
 * Save one seam's settings, over what the deployment supplies.
 *
 * A secret left out of `values` keeps whatever is stored — the screen never
 * received it, so it cannot send it back unchanged. Sending one as an empty
 * string is what clears it.
 */
export function saveIntegrationSeam(seam, values) {
  return api.put(endpoints.admin.integrationSeam(seam), { values });
}

/** Drop the saved overlay; the deployment's own values win again. */
export function revertIntegrationSeam(seam) {
  return api.delete(endpoints.admin.integrationSeam(seam));
}

/**
 * Ask the vendor, now, whether it answers.
 *
 * The one call on this screen that waits on a third party, and deliberately so:
 * it is the only way to find out whether the key somebody just typed is right.
 */
export function testIntegrationSeam(seam) {
  return api.post(endpoints.admin.integrationSeamTest(seam));
}
